import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { linkOptions, Outlet, useParams } from '@tanstack/react-router';
import { Anchor, ButtonLink, Icon, TextCutter } from '@anytag/shared-atoms';
import { TikTokAdReportType, useAllTiktokAdReportSummaryQuery } from '@anytag/graphql';
import { TikTokSummaryDataContext } from '@anytag/shared-organisms';
import { useGlobalLayoutProps } from '@anytag/shared-hooks';
import { cn } from '@anytag/shared-utils';

export const TikTokAdsReportRoot = ({ summaryData, loading }: TikTokSummaryDataContext) => {
  const params = useParams({ strict: false });
  const { t } = useTranslation();
  const { updateGlobalLayout } = useGlobalLayoutProps();

  const adCampaignId = params?.id || '';
  const adGroupId = params?.groupId || '';

  const [{ adCampaignName, adGroupName }, setState] = useState<{
    adGroupName?: string | null;
    adCampaignName?: string | null;
  }>({
    adGroupName: '',
    adCampaignName: ''
  });

  useAllTiktokAdReportSummaryQuery({
    skip: !adCampaignId,
    variables: { adReportType: TikTokAdReportType.AD_GROUP, adCampaignId },
    onCompleted: ({ allTiktokAdReportSummary }) => {
      if (allTiktokAdReportSummary?.adCampaignName) {
        setState((prev) => ({ ...prev, adCampaignName: allTiktokAdReportSummary.adCampaignName }));
      }
    }
  });
  useAllTiktokAdReportSummaryQuery({
    skip: !adCampaignId || !adGroupId,
    variables: { adReportType: TikTokAdReportType.AD, adCampaignId, adGroupId },
    onCompleted: ({ allTiktokAdReportSummary }) => {
      if (allTiktokAdReportSummary?.adGroupName) {
        setState((prevState) => ({ ...prevState, adGroupName: allTiktokAdReportSummary.adGroupName }));
      }
    }
  });

  useEffect(() => {
    if (summaryData?.numberCreatedCampaigns === 0) {
      updateGlobalLayout({ hasCustomHeader: true });
    }
  }, [summaryData?.numberCreatedCampaigns]);

  const goBackHref = adGroupId
    ? linkOptions({ to: '/tiktok/ad-campaign-report/$id', params: { id: adCampaignId } })
    : linkOptions({ to: '/tiktok/ad-campaign-report', params: undefined });

  return (
    <div>
      {summaryData?.numberCreatedCampaigns !== 0 && !loading ? (
        <header className="bg-gray-lv1 box-border flex h-(--header-height) items-center justify-start gap-2 p-4">
          {adGroupId || adCampaignId ? (
            <ButtonLink
              className="mr-2"
              variant="outline"
              to={goBackHref.to}
              params={goBackHref.params}
              prefixIcon={{ icon: 'caret-left', size: 11 }}
            />
          ) : null}
          <Anchor to={adCampaignId ? '/tiktok/ad-campaign-report' : undefined}>{t('appHeader.Ad Report')}</Anchor>
          {adCampaignId ? (
            <>
              <Icon icon="caret-right" size={9} variant="black" />
              <Anchor
                className={styles.title}
                to={adGroupId ? '/tiktok/ad-campaign-report/$id' : undefined}
                params={{ id: adCampaignId }}
              >
                <TextCutter text={adCampaignName} />
              </Anchor>
            </>
          ) : null}
          {adGroupId ? (
            <>
              <Icon icon="caret-right" size={9} variant="black" />
              <Anchor className={styles.title}>
                <TextCutter text={adGroupName} />
              </Anchor>
            </>
          ) : null}
        </header>
      ) : null}
      <Outlet />
    </div>
  );
};

const styles = {
  title: cn('box-border max-w-100 overflow-hidden text-xl font-semibold text-ellipsis')
};
